import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router'
import { PackageCheck, RefreshCw } from 'lucide-react'
import Card from '../../../components/common/Card'
import { api, apiHeaders } from '../../../configs/api'
import { useAuth } from '../../../context/AuthContext'
import DashboardFunnel from './DashboardFunnel'
import { DashboardData } from './dashboard.types'
import { FollowUpIndicator, PortfolioIndicator, ReminderAgendaIndicator } from './PipelineIndicators'
import SectionHeader from '../SectionHeader'

const DashboardOverview = () => {
    const { unity, token } = useAuth()
    const [data, setData] = useState<DashboardData | null>(null)
    const [selectedPipelineID, setSelectedPipelineID] = useState<number | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')
    const [reloadKey, setReloadKey] = useState(0)

    useEffect(() => {
        if (!unity) return

        const controller = new AbortController()
        const loadDashboard = async () => {
            setLoading(true)
            setError('')

            try {
                const response = await fetch(`${api}dashboard/getData`, {
                    method: 'POST',
                    headers: apiHeaders(token),
                    body: JSON.stringify({ unityID: unity.unityID }),
                    signal: controller.signal
                })
                const result = await response.json()

                if (!response.ok || !result.success) {
                    throw new Error(result.message || 'Não foi possível carregar a visão comercial.')
                }

                const dashboardData = result.data as DashboardData
                setData(dashboardData)
                setSelectedPipelineID(current =>
                    dashboardData.pipelines.some(pipeline => pipeline.id === current)
                        ? current
                        : dashboardData.pipelines[0]?.id ?? null
                )
            } catch (requestError) {
                if (requestError instanceof DOMException && requestError.name === 'AbortError') return
                setError(requestError instanceof Error ? requestError.message : 'Não foi possível carregar a visão comercial.')
            } finally {
                if (!controller.signal.aborted) setLoading(false)
            }
        }

        loadDashboard()
        return () => controller.abort()
    }, [reloadKey, token, unity])

    const selectedPipeline = useMemo(
        () => data?.pipelines.find(pipeline => pipeline.id === selectedPipelineID) ?? data?.pipelines[0] ?? null,
        [data, selectedPipelineID]
    )

    if (loading) {
        return (
            <div className='grid grid-cols-1 gap-4 xl:grid-cols-12'>
                <div className='h-[520px] animate-pulse rounded-2xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-white/[0.03] xl:col-span-8' />
                <div className='grid gap-4 md:grid-cols-2 xl:col-span-4 xl:grid-cols-1 xl:grid-rows-2'>
                    <div className='h-[252px] animate-pulse rounded-2xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-white/[0.03]' />
                    <div className='h-[252px] animate-pulse rounded-2xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-white/[0.03]' />
                </div>
                <div className='h-[180px] animate-pulse rounded-2xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-white/[0.03] xl:col-span-12' />
            </div>
        )
    }

    if (error) {
        return (
            <Card hasError>
                <div className='flex flex-col items-center gap-3 py-8 text-center'>
                    <p className='text-sm font-semibold text-gray-800 dark:text-gray-100'>{error}</p>
                    <button
                        type='button'
                        onClick={() => setReloadKey(current => current + 1)}
                        className='inline-flex items-center gap-2 rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-brand-600'
                    >
                        <RefreshCw className='h-4 w-4' /> Tentar novamente
                    </button>
                </div>
            </Card>
        )
    }

    if (!data?.defaultProduct) {
        return (
            <Card>
                <div className='flex flex-col items-center gap-3 py-10 text-center'>
                    <span className='rounded-2xl bg-brand-50 p-4 text-brand-700 dark:bg-brand-500/10 dark:text-brand-300'>
                        <PackageCheck className='h-7 w-7' />
                    </span>
                    <div>
                        <h2 className='font-semibold text-gray-900 dark:text-white'>Defina o produto padrão da unidade</h2>
                        <p className='mt-1 max-w-lg text-sm text-gray-500 dark:text-gray-400'>
                            O funil gerencial usa o produto padrão para manter uma visão comercial única e consistente.
                        </p>
                    </div>
                    <Link
                        to='/produto'
                        className='mt-1 rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-brand-600'
                    >
                        Acessar produtos
                    </Link>
                </div>
            </Card>
        )
    }

    if (!selectedPipeline) {
        return (
            <Card>
                <div className='py-10 text-center'>
                    <h2 className='font-semibold text-gray-900 dark:text-white'>Nenhum pipeline configurado</h2>
                    <p className='mt-1 text-sm text-gray-500 dark:text-gray-400'>
                        Crie um pipeline e suas etapas para visualizar o fluxo do produto {data.defaultProduct.name}.
                    </p>
                </div>
            </Card>
        )
    }

    return (
        <section aria-labelledby='commercial-overview-title'>
            <SectionHeader
                eyebrow='Visão comercial'
                title='Funil do produto padrão'
                titleID='commercial-overview-title'
                meta={
                    <div className='inline-flex items-center gap-2 rounded-full bg-brand-50 px-3 py-1 text-xs font-medium text-brand-700 dark:bg-brand-500/10 dark:text-brand-300'>
                        <PackageCheck className='h-3.5 w-3.5' /> {data.defaultProduct.name}
                    </div>
                }
                action={
                    data.pipelines.length > 1 ? (
                    <label className='text-xs font-medium text-gray-600 dark:text-gray-300'>
                        Pipeline
                        <select
                            value={selectedPipeline.id}
                            onChange={event => setSelectedPipelineID(Number(event.target.value))}
                            className='ml-2 rounded-lg border-gray-300 bg-white py-2 pl-3 pr-8 text-sm text-gray-800 shadow-theme-xs focus:border-brand-500 focus:ring-brand-500 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100'
                        >
                            {data.pipelines.map(pipeline => (
                                <option key={pipeline.id} value={pipeline.id}>
                                    {pipeline.label}
                                </option>
                            ))}
                        </select>
                    </label>
                    ) : undefined
                }
            />

            <div className='grid grid-cols-1 gap-4 xl:grid-cols-12'>
                <div className='xl:col-span-8'>
                    <Card className='h-full'>
                        <div className='flex flex-wrap items-start justify-between gap-2'>
                            <div>
                                <h3 className='font-semibold text-gray-900 dark:text-white'>{selectedPipeline.label}</h3>
                                <p className='mt-1 text-xs text-gray-500 dark:text-gray-400'>
                                    Quantidade atual de negócios em cada etapa
                                </p>
                            </div>
                            <span className='rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600 dark:bg-white/[0.06] dark:text-gray-300'>
                                {selectedPipeline.steps.length} {selectedPipeline.steps.length === 1 ? 'etapa' : 'etapas'}
                            </span>
                        </div>
                        <DashboardFunnel steps={selectedPipeline.steps} total={selectedPipeline.totalBusinesses} />
                    </Card>
                </div>

                <div className='grid gap-4 md:grid-cols-2 xl:col-span-4 xl:grid-cols-1 xl:grid-rows-2'>
                    <PortfolioIndicator pipeline={selectedPipeline} />
                    <FollowUpIndicator pipeline={selectedPipeline} />
                </div>

                <div className='xl:col-span-12'>
                    <ReminderAgendaIndicator pipeline={selectedPipeline} />
                </div>
            </div>
        </section>
    )
}

export default DashboardOverview
